"use client";

/* eslint-disable no-console */
import React, { Fragment, useEffect, useRef, useState } from "react";
import lottie from "lottie-web";
import styled from "styled-components";

import ActivityIndicator from "../ActivityIndicator";
import Box from "../Box";
import Notice from "../Notice";

import { getColor } from "../utils";
import { callApiWithRawResponse } from "../../helpers/callApi";

type Props = {
  permalink: string;
};

const LottieContainer = styled(Box)<{ loaded: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
  ${props =>
    props.loaded &&
    `
    opacity: 1;
  `}
`;

const AbsoluteContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LottieAnimation: React.FC<Props> = ({ permalink }) => {
  const lottieContainerRef = useRef();
  const [animationData, setAnimationData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!permalink.includes("/v4/")) console.warn("Lottie permalink must be a valid API url");

  let endpoint = permalink.split("/v4/")[1];
  if (endpoint.includes("?")) endpoint += "&response=data";
  else endpoint += "?response=data";

  /**
   * Need to fetch the raw json from our asset permalink
   */
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    callApiWithRawResponse({ endpoint })
      .then(data => {
        setAnimationData(data);
        setLoaded(true);
      })
      .catch(err => {
        console.log({ err });
        setFailed(true);
      });
  }, [endpoint, permalink]);

  /**
   * Once asset json has been fetched, initialize the animation
   */
  useEffect(() => {
    if (!animationData) return;

    try {
      const lottieInstance = lottie.loadAnimation({
        container: lottieContainerRef.current,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData,
      });

      /**
       * If the JSON can be fetched, but it's invalid upon loading
       */
      lottieInstance.addEventListener("data_failed", () => setFailed(true));

      return () => {
        lottieInstance.destroy();
      };
    } catch (err) {
      console.error("Failed to initialize Lottie animation:", err);
      setFailed(true);
    }
  }, [animationData, permalink]);

  return (
    <Fragment>
      {!loaded && !failed && (
        <AbsoluteContainer>
          <ActivityIndicator size="small" color={getColor("grayDark")} center />
        </AbsoluteContainer>
      )}
      {failed && (
        <AbsoluteContainer>
          <Notice noticeStyle="error" message="Oops! Failed to load Lottie JSON." />
        </AbsoluteContainer>
      )}
      <LottieContainer loaded={loaded} ref={lottieContainerRef} />
    </Fragment>
  );
};

export default LottieAnimation;
